import axios from "@/utils/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type LegalInfoFilters,
  type CreateLegalType,
  type ApiLegalResponseType,
  type UpdateLegalType,
  // ApiLegalResponseSchema,
} from "@/types/entities/legal";

export const useGetPrivacyPolicies = (filters?: LegalInfoFilters) => {
  return useQuery({
    queryKey: ["admin-legal", filters],
    queryFn: async (): Promise<ApiLegalResponseType> => {
      const response = await axios.get(`/admin/v1/privacy-policies`, {
        params: filters,
      });

      // const result = ApiLegalResponseSchema.safeParse(response);
      // if (!result.success) {
      //   console.error("Validation error:", result.error);
      //   throw new Error("Invalid response structure from API");
      // }
      return response.data;
    },
  });
};

export const useCreatePrivacyPolicy = (filters?: LegalInfoFilters) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-admin-legal"],
    mutationFn: async (data: CreateLegalType) => {
      const response = await axios.post("/admin/v1/privacy-policies", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-legal", filters],
      });
    },
  });
};

export const useUpdatePrivacyPolicy = (filters?: LegalInfoFilters) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-admin-legal"],
    mutationFn: async (data: UpdateLegalType) => {
      const response = await axios.patch(
        `/admin/v1/privacy-policies/${data.id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-legal", filters],
      });
    },
  });
};

// Terms and Conditions APIs - Applicant Conditions Endpoints
export const useGetTermsConditions = (filters?: LegalInfoFilters) => {
  return useQuery({
    queryKey: ["admin-terms", filters],
    queryFn: async (): Promise<ApiLegalResponseType> => {
      const response = await axios.get(`/admin/v1/applicant-conditions`, {
        params: filters,
      });
      return response.data;
    },
  });
};

export const useCreateTermsConditions = (filters?: LegalInfoFilters) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-admin-terms"],
    mutationFn: async (data: CreateLegalType) => {
      const response = await axios.post("/admin/v1/applicant-conditions", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-terms", filters],
      });
    },
  });
};

export const useUpdateTermsConditions = (filters?: LegalInfoFilters) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-admin-terms"],
    mutationFn: async (data: UpdateLegalType) => {
      const response = await axios.put(
        `/admin/v1/applicant-conditions/${data.id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-terms", filters],
      });
    },
  });
};

export const useDeleteTermsConditions = (filters?: LegalInfoFilters) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-admin-terms"],
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/admin/v1/applicant-conditions/${id}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-terms", filters],
      });
    },
  });
};

// Public endpoint - Get current active terms and conditions
export const useGetCurrentTermsConditions = () => {
  return useQuery({
    queryKey: ["public-terms-current"],
    queryFn: async () => {
      const response = await axios.get(`/auth/v1/applicant-conditions/current`);
      return response.data;
    },
  });
};
